/**
 * Printable form component for shipping details
 * Renders a print-friendly version of shipping information
 */

'use client';

import React, { FC } from 'react';
import { Box, Typography, Stack, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { formatDateForPrint } from './shippingUtils';
import { ShippingDto } from '@/api/ApiClient';
import { formatMaterials, formatSubstrate } from '@/lib/utils/sampleUtils';
import { useSystemConfig } from '@/hooks/useSystemConfig';

interface ShippingPrintableFormProps {
  shipping: ShippingDto;
  isLocalTransfer: boolean;
}

export const ShippingPrintableForm: FC<ShippingPrintableFormProps> = ({ shipping, isLocalTransfer }) => {
  const systemConfig = useSystemConfig();

  if (isLocalTransfer) {
    return null; // Local transfers don't have printable form
  }

  const sampleCount = shipping.samples?.length ?? 0;
  const orgName = systemConfig?.orgName?.trim() || 'Organization';
  const orgLogoAlt = `${orgName} logo`;

  return (
    <Box className="shipping-print-content">
      <Stack spacing={2} sx={{ p: 2 }}>
        <Box className="shipping-print-header" sx={{ mb: 1, width: '100%' }}>
          <Typography variant="h6" component="h1" sx={{ fontWeight: 600 }}>
            Shipping {shipping.id ?? ''} for Project {shipping.projectLabel ?? 'N/A'}
          </Typography>
          <Box className="shipping-print-logo-wrap">
            <Box className="shipping-print-logo-list">
              <Box component="img" src="/images/list_logo.png" alt="LiST — Lifetime Sample Tracking" />
            </Box>
            {systemConfig?.logoPath ? (
              <Box className="shipping-print-logo-org">
                {/* Match NavBar: organization logo URL comes from system config */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={systemConfig.logoPath} alt={orgLogoAlt} />
              </Box>
            ) : null}
          </Box>
        </Box>

        {/* Top summary: align with edit view (project, order, sample count; then shipping logistics). */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="body2" fontWeight="bold">
              Project
            </Typography>
            <Typography variant="body2">{shipping.projectLabel ?? 'N/A'}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="body2" fontWeight="bold">
              Internal Order Number
            </Typography>
            <Typography variant="body2">{shipping.budget ?? 'N/A'}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="body2" fontWeight="bold">
              # Samples
            </Typography>
            <Typography variant="body2">{sampleCount}</Typography>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="body2" fontWeight="bold">
              Shipping type
            </Typography>
            <Typography variant="body2">{shipping.shippingType ?? 'N/A'}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="body2" fontWeight="bold">
              Shipping date
            </Typography>
            <Typography variant="body2">{formatDateForPrint(shipping.date)}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="body2" fontWeight="bold">
              Tracking number
            </Typography>
            <Typography variant="body2">{shipping.trackingID?.trim() ? shipping.trackingID : 'N/A'}</Typography>
          </Grid>
        </Grid>

        {/* Sender (left) and recipient (right), matching the main shipping detail layout. */}
        <Box className="shipping-panels-container shipping-print-panels">
          <Paper elevation={0} variant="outlined" className="shipping-panel shipping-paper">
            <Typography variant="subtitle1" gutterBottom>
              Sender
            </Typography>
            <Stack spacing={2} className="shipping-panel-content">
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Researcher
                </Typography>
                <Typography variant="body2">{shipping.researcher ?? 'N/A'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Email
                </Typography>
                <Typography variant="body2">{shipping.researcherEmail ?? 'N/A'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Phone
                </Typography>
                <Typography variant="body2">{shipping.researcherPhone ?? 'N/A'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Return address
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-line' }}
                  dangerouslySetInnerHTML={{
                    __html: shipping.returnAddress?.replace(/\n/g, '<br>') || 'N/A',
                  }}
                />
              </Box>
            </Stack>
          </Paper>

          <Paper elevation={0} variant="outlined" className="shipping-panel shipping-paper">
            <Typography variant="subtitle1" gutterBottom>
              Ship To
            </Typography>
            <Stack spacing={2} className="shipping-panel-content">
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Contact Name
                </Typography>
                <Typography variant="body2">{shipping.contactName ?? 'N/A'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Contact Email
                </Typography>
                <Typography variant="body2">{shipping.contactEmail ?? 'N/A'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Contact Phone
                </Typography>
                <Typography variant="body2">{shipping.contactPhone ?? 'N/A'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Shipping address
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-line' }}
                  dangerouslySetInnerHTML={{
                    __html: shipping.address?.replace(/\n/g, '<br>') || 'N/A',
                  }}
                />
              </Box>
            </Stack>
          </Paper>
        </Box>

        {shipping.comment ? (
          <Box>
            <Typography variant="body2" fontWeight="bold">
              Comment
            </Typography>
            <Typography variant="body2" component="div" dangerouslySetInnerHTML={{ __html: shipping.comment }} />
          </Box>
        ) : null}

        {/* Samples table in printable form */}
        {shipping.samples && shipping.samples.length > 0 ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Samples
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Sample</TableCell>
                  <TableCell>Material</TableCell>
                  <TableCell>Substrate</TableCell>
                  <TableCell>Growth Date</TableCell>
                  <TableCell>Label Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shipping.samples.map((sampleLine, index) => {
                  const sample = sampleLine.sample;
                  return (
                    <React.Fragment key={sampleLine.sampleId}>
                      <TableRow>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{sample?.sampleLabel ?? 'N/A'}</TableCell>
                        <TableCell>{formatMaterials(sample?.materials)}</TableCell>
                        <TableCell>{formatSubstrate(sample)}</TableCell>
                        <TableCell>{formatDateForPrint(sample?.creationDate)}</TableCell>
                        <TableCell>{sampleLine.labelType || 'N/A'}</TableCell>
                      </TableRow>
                      {sample?.description ? (
                        <TableRow>
                          <TableCell></TableCell>
                          <TableCell colSpan={5}>
                            <Box
                              dangerouslySetInnerHTML={{
                                __html: sample.description,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
};
